import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const fmt = d => d ? new Date(d).toLocaleString() : '—';
// Launch the installed RustDesk client and show the credentials to copy
function connect(link, id, pw, server) {
  if (link) window.location.href = link;
  const msg = `RustDesk ID: ${id}${pw ? `\nPassword: ${pw}` : ''}`
    + `${server ? `\nServer: ${server}` : ''}`
    + `\n\nIf RustDesk didn't open, open it manually (pointed at ${server || 'your server'}) and enter this ID.`;
  setTimeout(() => window.alert(msg), 400);
}

export default function RemoteSupport() {
  const [tab, setTab] = useState('requests');
  return (
    <div>
      <div style={{ marginBottom:14 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>🖥 Remote Support</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>RustDesk remote sessions — on-demand requests and saved clients.</p>
      </div>
      <div className="flex-gap" style={{ gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {[['requests','🙋 Requests'],['devices','🖥 Registered Devices'],['clients','💻 Permanent Clients'],['logs','📜 Logs'],['config','⚙ Configuration']].map(([id,l]) => (
          <button key={id} className={`btn btn-sm ${tab===id?'btn-primary':'btn-secondary'}`} onClick={() => setTab(id)}>{l}</button>
        ))}
      </div>
      {tab === 'requests' && <Requests />}
      {tab === 'devices'  && <Devices />}
      {tab === 'clients'  && <Clients />}
      {tab === 'logs'     && <Logs />}
      {tab === 'config'   && <Configuration />}
    </div>
  );
}

function Requests() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/remote/requests').then(r => setRows(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const doConnect = async r => {
    try { const d = (await api.post(`/remote/requests/${r.id}/connect`)).data; connect(d.link, d.rustdesk_id, d.password, d.server); load(); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not connect'); }
  };
  const close = async r => { await api.post(`/remote/requests/${r.id}/close`); load(); };
  if (!rows.length) return <div className="card"><div className="empty-state"><p>No open remote-support requests.</p></div></div>;
  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <div className="table-wrap"><table>
        <thead><tr><th>Requested</th><th>User</th><th>Campus</th><th>RustDesk ID</th><th>Issue</th><th>Status</th><th></th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id}>
            <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{fmt(r.created_at)}</td>
            <td style={{ fontSize:13, fontWeight:500 }}>{r.requester_name}</td>
            <td style={{ fontSize:12 }}>{r.campus_name || '—'}</td>
            <td className="mono" style={{ fontSize:12 }}>{r.rustdesk_id || <span style={{ color:'var(--text3)' }}>awaiting user</span>}</td>
            <td style={{ fontSize:12, color:'var(--text2)', maxWidth:220 }}>{r.issue || '—'}</td>
            <td><span className={`badge ${r.status==='connected'?'badge-completed':r.status==='ready'?'badge-repair':'badge-pending'}`}>{r.status}</span></td>
            <td><div className="flex-gap" style={{ gap:6 }}>
              <button className="btn btn-primary btn-sm" disabled={!r.rustdesk_id} onClick={() => doConnect(r)}>Connect</button>
              <button className="btn btn-secondary btn-sm" onClick={() => close(r)}>Close</button>
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>
    </div>
  );
}

// Devices that have registered against our RustDesk server (read from hbbs peer DB)
function Devices() {
  const [state, setState] = useState({ loading:true });
  const [server, setServer] = useState(null);
  const load = useCallback(() => {
    api.get('/remote/devices')
      .then(r => setState({ loading:false, ...r.data }))
      .catch(() => setState({ loading:false, available:false, devices:[], error:'Could not load devices' }));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get('/remote/settings').then(r => setServer(r.data || {})).catch(()=>{}); }, []);

  const quickConnect = d => connect(`rustdesk://connect/${d.rustdesk_id}`, d.rustdesk_id, null, server?.server_host);
  const saveAsClient = async d => {
    const name = window.prompt('Name this device (e.g. Lab 3 Teacher PC):', d.name || '');
    if (!name || !name.trim()) return;
    try { await api.post('/remote/clients', { name: name.trim(), rustdesk_id: d.rustdesk_id }); load(); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not save'); }
  };

  if (state.loading) return <div className="card"><div className="empty-state"><p>Loading devices…</p></div></div>;
  if (!state.available) return (
    <div className="card"><div className="empty-state">
      <p>Device list unavailable.</p>
      <p style={{ fontSize:12, color:'var(--text2)' }}>{state.error || 'The RustDesk server database is not reachable from the portal.'}</p>
    </div></div>
  );
  if (!state.devices.length) return <div className="card"><div className="empty-state"><p>No devices have registered against the server yet.</p></div></div>;

  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <div style={{ fontSize:12, color:'var(--text2)', padding:'10px 12px' }}>
        Every RustDesk ID that has configured against your server. Save one as a Permanent Client to give it a name.
      </div>
      <div className="table-wrap"><table>
        <thead><tr><th>RustDesk ID</th><th>First Seen</th><th>Status</th><th></th></tr></thead>
        <tbody>{state.devices.map(d => (
          <tr key={d.rustdesk_id}>
            <td className="mono" style={{ fontSize:13, fontWeight:600 }}>{d.rustdesk_id}</td>
            <td style={{ fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>{fmt(d.first_seen)}</td>
            <td>{d.saved
              ? <span className="badge badge-completed">Saved · {d.name}</span>
              : <span className="badge badge-pending">New</span>}</td>
            <td><div className="flex-gap" style={{ gap:6 }}>
              <button className="btn btn-primary btn-sm" onClick={() => quickConnect(d)}>Connect</button>
              {!d.saved && <button className="btn btn-secondary btn-sm" onClick={() => saveAsClient(d)}>Save as Client</button>}
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>
    </div>
  );
}

const blank = { name:'', campus_id:'', rustdesk_id:'', rustdesk_pw:'', location:'', notes:'' };
function Clients() {
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get('/remote/clients', { params: search ? { search } : {} }).then(r => setRows(r.data)).catch(()=>{}), [search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{}); }, []);
  const save = async () => {
    if (!modal.name.trim() || !modal.rustdesk_id.trim()) { setErr('Name and RustDesk ID are required'); return; }
    setBusy(true); setErr('');
    try {
      if (modal.id) await api.put(`/remote/clients/${modal.id}`, modal);
      else await api.post('/remote/clients', modal);
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const doConnect = async c => {
    try { const d = (await api.post(`/remote/clients/${c.id}/connect`)).data; connect(d.link, d.rustdesk_id, d.password, d.server); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not connect'); }
  };
  const del = async c => { if (window.confirm(`Remove client "${c.name}"?`)) { await api.delete(`/remote/clients/${c.id}`); load(); } };

  return (
    <div>
      <div className="flex-gap" style={{ gap:8, marginBottom:12, justifyContent:'space-between', flexWrap:'wrap' }}>
        <input placeholder="Search name / ID / location…" value={search} onChange={e => setSearch(e.target.value)} style={{ padding:'6px 8px', minWidth:220 }} />
        <button className="btn btn-primary" onClick={() => { setModal({ ...blank }); setErr(''); }}>+ Add Client</button>
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No saved clients yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Name</th><th>Campus</th><th>Location</th><th>RustDesk ID</th><th>Last Connected</th><th></th></tr></thead>
            <tbody>{rows.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight:500 }}>{c.name}</td>
                <td style={{ fontSize:12 }}>{c.campus_name || '—'}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{c.location || '—'}</td>
                <td className="mono" style={{ fontSize:12 }}>{c.rustdesk_id}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{fmt(c.last_connected_at)}</td>
                <td><div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => doConnect(c)}>Connect</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setModal({ ...blank, ...c, campus_id:String(c.campus_id||'') }); setErr(''); }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => del(c)}>Delete</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:520 }}>
            <div className="modal-header"><span className="modal-title">{modal.id ? 'Edit Client' : 'Add Client'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-group"><label>Name *</label>
              <input value={modal.name} onChange={e => setModal(m => ({ ...m, name:e.target.value }))} placeholder="e.g. Lab 3 Teacher PC" /></div>
            <div className="form-row">
              <div className="form-group"><label>Campus</label>
                <select value={modal.campus_id} onChange={e => setModal(m => ({ ...m, campus_id:e.target.value }))}>
                  <option value="">—</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="form-group"><label>Location</label>
                <input value={modal.location} onChange={e => setModal(m => ({ ...m, location:e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>RustDesk ID *</label>
                <input value={modal.rustdesk_id} onChange={e => setModal(m => ({ ...m, rustdesk_id:e.target.value }))} placeholder="9-digit ID" /></div>
              <div className="form-group"><label>Permanent Password</label>
                <input value={modal.rustdesk_pw} onChange={e => setModal(m => ({ ...m, rustdesk_pw:e.target.value }))} /></div>
            </div>
            <div className="form-group"><label>Notes</label>
              <textarea rows={2} value={modal.notes} onChange={e => setModal(m => ({ ...m, notes:e.target.value }))} /></div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Server address + key that IT sets into each RustDesk client, plus the client download.
function Configuration() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => { api.get('/remote/settings').then(r => setS(r.data || {})).catch(()=>setS({})); }, []);
  useEffect(() => {
    api.get('/auth/me').then(r => setCanEdit(['admin','it_head'].includes(r.data?.user?.role))).catch(()=>{});
  }, []);
  if (!s) return null;
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  const copy = t => { navigator.clipboard?.writeText(t || ''); setMsg('Copied'); setTimeout(()=>setMsg(''),1500); };
  const save = async () => {
    setBusy(true); setMsg('');
    try { await api.put('/remote/settings', s); setMsg('✓ Saved'); setTimeout(()=>setMsg(''),2000); }
    catch (e) { setMsg(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const host = (s.server_host || '').trim();
  const key = (s.public_key || '').trim();
  return (
    <div className="card" style={{ maxWidth:640 }}>
      <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>RustDesk Client Setup</div>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:16 }}>
        Install RustDesk on the machine, then in <b>RustDesk → Settings → Network → ID/Relay Server</b> enter the
        server &amp; key below. Firewall on the server must allow TCP&nbsp;21115–21117 and UDP&nbsp;21116.
      </div>

      <div style={{ marginBottom:16 }}>
        <a className="btn btn-primary" href="https://rustdesk.com/download" target="_blank" rel="noreferrer">⬇ Download RustDesk Client</a>
      </div>

      <div className="form-group"><label>ID / Server Host</label>
        <div className="flex-gap" style={{ gap:6 }}>
          <input value={host} disabled={!canEdit} onChange={e => set('server_host', e.target.value)} placeholder="itsupport.tipsglobeducate.com" style={{ flex:1 }} />
          <button className="btn btn-secondary btn-sm" onClick={() => copy(host)}>Copy</button>
        </div>
      </div>
      <div className="form-group"><label>Key <span style={{ fontSize:11, color:'var(--text2)' }}>(server's id_ed25519.pub)</span></label>
        <div className="flex-gap" style={{ gap:6 }}>
          <input className="mono" value={key} disabled={!canEdit} onChange={e => set('public_key', e.target.value)} placeholder="57CvTxj6Bv591Qiz…=" style={{ flex:1 }} />
          <button className="btn btn-secondary btn-sm" onClick={() => copy(key)}>Copy</button>
        </div>
      </div>

      {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')||msg==='Copied'?'var(--green)':'var(--red)', marginBottom:8 }}>{msg}</div>}
      {canEdit
        ? <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'…':'Save Configuration'}</button>
        : <div style={{ fontSize:11, color:'var(--text2)' }}>Only IT Admin / IT Head can edit these values.</div>}
    </div>
  );
}

function Logs() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/remote/logs').then(r => setRows(r.data)).catch(()=>{}); }, []);
  if (!rows.length) return <div className="card"><div className="empty-state"><p>No activity yet.</p></div></div>;
  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <div className="table-wrap"><table>
        <thead><tr><th>When</th><th>Action</th><th>Target</th><th>RustDesk ID</th><th>Campus</th><th>By</th></tr></thead>
        <tbody>{rows.map(l => (
          <tr key={l.id}>
            <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{fmt(l.created_at)}</td>
            <td><span className={`badge ${l.action==='connect'?'badge-completed':l.action==='closed'?'badge-normal':'badge-pending'}`}>{l.action}</span>
              <span style={{ fontSize:11, color:'var(--text2)' }}> · {l.source}</span></td>
            <td style={{ fontSize:13 }}>{l.target_name || '—'}</td>
            <td className="mono" style={{ fontSize:12 }}>{l.rustdesk_id || '—'}</td>
            <td style={{ fontSize:12 }}>{l.campus_name || '—'}</td>
            <td style={{ fontSize:12 }}>{l.by_name || '—'}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </div>
  );
}
